import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';

import { useAuth } from '../../contexts/AuthContext';
import { formatDateTimeInBusinessTZ } from '../../utils/date';
import { analytics } from '../../utils/analytics';
import { BRAND_KEYS, DEFAULT_BRAND_KEY, type BrandKey } from '../../config/brands';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { templateUsesBrandVariables, withoutBrandVariables } from '../utils/emailBrand';

// Toast notification component
const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  const icon = type === 'success' ? '✅' : '❌';

  return (
    <div className={`fixed top-4 right-4 z-50 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-80`}>
      <span className="text-lg">{icon}</span>
      <span className="flex-1">{message}</span>
      <button
        onClick={onClose}
        className="text-white hover:text-gray-200 text-xl font-bold"
      >
        ×
      </button>
    </div>
  );
};

interface EmailTemplate {
  id: string;
  template_key: string;
  name: string;
  description: string;
  subject_template: string;
  html_template: string;
  text_template: string;
  sample_data: Record<string, unknown>;
  variables: string[];
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface TemplateVersion {
  id: string;
  template_id: string;
  version: number;
  html_template: string;
  text_template: string;
  subject_template: string;
  sample_data: Record<string, unknown>;
  variables: string[];
  created_at: string;
  created_by: string;
}

export function AdminEmailTemplates() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showVersions, setShowVersions] = useState(false);
  const [testEmailAddresses, setTestEmailAddresses] = useState('');
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [htmlErrors, setHtmlErrors] = useState<string[]>([]);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Which brand Preview and Send Test Email render as. Only offered for a template that names its
  // brand; any other template renders as the default, exactly as before.
  const [previewBrand, setPreviewBrand] = useState<BrandKey>(DEFAULT_BRAND_KEY);
  const brandAware = selectedTemplate ? templateUsesBrandVariables(selectedTemplate) : false;
  const effectiveBrand: BrandKey = brandAware ? previewBrand : DEFAULT_BRAND_KEY;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    subject_template: '',
    html_template: '',
    text_template: '',
    sample_data: '{}',
    variables: '[]'
  });

  // Check admin status
  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ['admin-status'],
    queryFn: async () => {
      console.log('Checking admin status for user:', user?.email);
      const { data, error } = await supabase
        .from('admin_users')
        .select('email')
        .eq('email', user?.email)
        .maybeSingle();
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      const adminStatus = !!data;
      console.log('Admin status result:', adminStatus, 'data:', data);
      return adminStatus;
    },
    enabled: !!user?.email,
  });

  // Fetch email templates
  const { data: templates, isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch template versions
  const { data: versions } = useQuery<TemplateVersion[]>({
    queryKey: ['template-versions', selectedTemplate?.id],
    queryFn: async () => {
      if (!selectedTemplate) return [];
      
      const { data, error } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('template_id', selectedTemplate.id)
        .order('version', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedTemplate && showVersions
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: async (templateData: Partial<EmailTemplate>) => {
      if (!selectedTemplate) throw new Error('No template selected');
      
      const { data, error } = await supabase
        .from('email_templates')
        .update({
          ...templateData,
          sample_data: typeof templateData.sample_data === 'string' 
            ? JSON.parse(templateData.sample_data) 
            : templateData.sample_data,
          variables: typeof templateData.variables === 'string'
            ? JSON.parse(templateData.variables)
            : templateData.variables,
          updated_by: user?.id
        })
        .eq('id', selectedTemplate.id)
        .select()
        .single();
      
      if (error) throw error;

      // Templates are versioned in email_template_versions, but nothing recorded *that* an
      // edit happened in the same place every other admin action is reviewed.
      analytics.trackAdminAction('admin_email_template_update', {
        entityType: 'email_template',
        entityId: selectedTemplate.id,
        metadata: {
          template_key: selectedTemplate.template_key,
          changed_fields: Object.keys(templateData),
        },
      });
      return data;
    },
    onSuccess: (updatedTemplate) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setSelectedTemplate(updatedTemplate);
      setIsEditing(false);
    },
    onError: (error) => {
      console.error('Error updating template:', error);
      alert('Failed to update template. Please check the console for details.');
    }
  });

  // Send test email mutation
  const sendTestEmailMutation = useMutation({
    mutationFn: async ({ email, templateKey, brand }: { email: string | string[]; templateKey: string; brand: BrandKey }) => {
      const sample = selectedTemplate?.sample_data || {};
      // For another brand, drop the stored default-brand samples so send-email fills the brand
      // variables (and the sender name) from `brand`. The default brand sends exactly as before.
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: brand === DEFAULT_BRAND_KEY
          ? { type: 'template', templateKey, to: email, data: sample }
          : { type: 'template', templateKey, to: email, data: withoutBrandVariables(sample), brand },
      });
      
      if (error) throw error;

      // A test send can be addressed to anyone, including a real parent.
      analytics.trackAdminAction('admin_email_test_send', {
        entityType: 'email_template',
        entityId: selectedTemplate?.id,
        metadata: {
          template_key: templateKey,
          brand,
          recipient_count: Array.isArray(email) ? email.length : 1,
          recipients: Array.isArray(email) ? email : [email],
        },
      });
      return data;
    },
    onSuccess: (_, variables) => {
      const recipientCount = Array.isArray(variables.email) ? variables.email.length : 1;
      setToast({ message: `Test email sent successfully to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}!`, type: 'success' });
      setShowTestEmailDialog(false);
      setTestEmailAddresses('');
    },
    onError: (error) => {
      console.error('Error sending test email:', error);
      setToast({ message: 'Failed to send test email. Please check the console for details.', type: 'error' });
    }
  });

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async (templateId: string) => {
      if (!selectedTemplate) throw new Error('No template selected');
      
      console.log('Current user:', user);
      console.log('Attempting to update template:', templateId, 'new active state:', !selectedTemplate.is_active);
      
      const { data, error } = await supabase
        .from('email_templates')
        .update({ 
          is_active: !selectedTemplate.is_active,
          updated_by: user?.id 
        })
        .eq('id', templateId)
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }
      return data;
    },
    onSuccess: (updatedTemplate) => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setSelectedTemplate(updatedTemplate);
      setToast({ 
        message: `Template ${updatedTemplate.is_active ? 'activated' : 'deactivated'} successfully!`, 
        type: 'success' 
      });
    },
    onError: (error) => {
      console.error('Error toggling template status:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      
      // More specific error messages
      let errorMessage = 'Failed to update template status.';
      if (error?.message?.includes('403') || (error as { code?: string })?.code === 'PGRST103') {
        errorMessage = 'Access denied. You may not have admin permissions to update email templates.';
      } else if (error?.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setToast({ message: errorMessage, type: 'error' });
    }
  });

  // HTML validation function
  const validateHtml = (html: string): string[] => {
    const errors: string[] = [];
    
    // Basic HTML validation
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const parserErrors = doc.querySelectorAll('parsererror');
    
    if (parserErrors.length > 0) {
      errors.push('Invalid HTML syntax detected');
    }
    
    // Check for common HTML issues
    const openTags = html.match(/<[^/][^>]*>/g) || [];
    const closeTags = html.match(/<\/[^>]*>/g) || [];
    
    // Simple tag matching (not perfect but catches obvious issues)
    const tagCounts: Record<string, number> = {};
    
    openTags.forEach(tag => {
      const tagName = tag.match(/<(\w+)/)?.[1]?.toLowerCase();
      if (tagName && !['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'].includes(tagName)) {
        tagCounts[tagName] = (tagCounts[tagName] || 0) + 1;
      }
    });
    
    closeTags.forEach(tag => {
      const tagName = tag.match(/<\/(\w+)/)?.[1]?.toLowerCase();
      if (tagName) {
        tagCounts[tagName] = (tagCounts[tagName] || 0) - 1;
      }
    });
    
    Object.entries(tagCounts).forEach(([tag, count]) => {
      if (count > 0) {
        errors.push(`Unclosed <${tag}> tag detected`);
      } else if (count < 0) {
        errors.push(`Extra </${tag}> closing tag detected`);
      }
    });
    
    // Check for inline styles vs CSS (email best practice)
    if (html.includes('<style>') || html.includes('<link')) {
      errors.push('Warning: Use inline styles for better email compatibility');
    }
    
    return errors;
  };

  // Load form data when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setFormData({
        name: selectedTemplate.name,
        description: selectedTemplate.description || '',
        subject_template: selectedTemplate.subject_template,
        html_template: selectedTemplate.html_template,
        text_template: selectedTemplate.text_template || '',
        sample_data: JSON.stringify(selectedTemplate.sample_data, null, 2),
        variables: JSON.stringify(selectedTemplate.variables, null, 2)
      });
    }
  }, [selectedTemplate]);

  // Validate HTML when it changes
  useEffect(() => {
    if (formData.html_template) {
      const errors = validateHtml(formData.html_template);
      setHtmlErrors(errors);
    }
  }, [formData.html_template]);

  // Generate preview using the actual email function
  const generatePreview = async () => {
    if (!selectedTemplate) return;
    
    try {
      // Call the send-email function's preview capability
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: effectiveBrand === DEFAULT_BRAND_KEY
          ? { type: 'preview', templateKey: selectedTemplate.template_key }
          : { type: 'preview', templateKey: selectedTemplate.template_key, brand: effectiveBrand },
      });
      
      if (error) {
        console.error('Preview error:', error);
        alert('Error generating preview. Please check the console for details.');
        return;
      }
      
      if (data?.html) {
        setPreviewHtml(data.html);
        setShowPreview(true);
      } else {
        alert('No preview HTML returned from the function.');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Error generating preview. Please check the console for details.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate and parse JSON fields
      const parsedSampleData = JSON.parse(formData.sample_data);
      const parsedVariables = JSON.parse(formData.variables);
      
      // Create properly typed update object
      const updateData: Partial<EmailTemplate> = {
        name: formData.name,
        description: formData.description,
        subject_template: formData.subject_template,
        html_template: formData.html_template,
        text_template: formData.text_template,
        sample_data: parsedSampleData,
        variables: parsedVariables
      };
      
      updateTemplateMutation.mutate(updateData);
    } catch (error) {
      alert('Invalid JSON in sample data or variables field.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">Loading email templates...</div>
    );
  }

  // Early return for loading or non-admin users
  if (adminLoading || isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-lg text-red-600 mb-2">⚠️ Access Denied</div>
            <div className="text-gray-500">You need admin permissions to access email templates.</div>
            <div className="text-sm text-gray-400 mt-2">User: {user?.email}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex-1 min-h-0 overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">📧 Email Templates</h1>
        </div>
        <div className="flex gap-3">
          {selectedTemplate && (
            <>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Active</span>
                <button
                  onClick={() => toggleActiveMutation.mutate(selectedTemplate.id)}
                  disabled={toggleActiveMutation.isPending}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                    selectedTemplate.is_active 
                      ? 'bg-blue-600' 
                      : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      selectedTemplate.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <button
                onClick={() => setShowVersions(!showVersions)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                📚 Versions ({selectedTemplate.version})
              </button>
              {brandAware && (
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  As brand
                  <select
                    value={previewBrand}
                    onChange={(e) => setPreviewBrand(e.target.value as BrandKey)}
                    className="border-gray-300 rounded-md text-sm"
                  >
                    {BRAND_KEYS.map((key) => (
                      <option key={key} value={key}>{BRAND_PROFILES[key].shortName}</option>
                    ))}
                  </select>
                </label>
              )}
              <button
                onClick={generatePreview}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                👁️ Preview
              </button>
              <button
                onClick={() => setShowTestEmailDialog(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
              >
                📧 Send Test Email
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates List */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Templates</h3>
              <div className="space-y-2">
                {templates?.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setIsEditing(false);
                      setShowPreview(false);
                    }}
                    className={`p-3 rounded-lg cursor-pointer border ${
                      selectedTemplate?.id === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${!template.is_active ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className={`text-sm font-medium ${
                            template.is_active ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {template.name}
                          </h4>
                          {!template.is_active && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              INACTIVE
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {template.template_key}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          v{template.version} • {formatDateTimeInBusinessTZ(template.updated_at)}
                        </p>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${
                        template.is_active ? 'bg-green-400' : 'bg-gray-300'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Version History */}
          {showVersions && selectedTemplate && (
            <div className="bg-white shadow rounded-lg mt-4">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Version History</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {versions?.map((version) => (
                    <div
                      key={version.id}
                      className="p-3 rounded-lg border border-gray-200 hover:border-gray-300"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            Version {version.version}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDateTimeInBusinessTZ(version.created_at)}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            // Restore this version
                            setFormData({
                              name: selectedTemplate.name,
                              description: selectedTemplate.description || '',
                              subject_template: version.subject_template,
                              html_template: version.html_template,
                              text_template: version.text_template || '',
                              sample_data: JSON.stringify(version.sample_data, null, 2),
                              variables: JSON.stringify(version.variables, null, 2)
                            });
                            setIsEditing(true);
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                        >
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedTemplate.name}
                  </h3>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                      isEditing
                        ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                        : 'text-white bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isEditing ? 'Cancel' : '✏️ Edit'}
                  </button>
                </div>

                {isEditing ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Template Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={2}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Subject Template
                      </label>
                      <input
                        type="text"
                        value={formData.subject_template}
                        onChange={(e) => setFormData({ ...formData, subject_template: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Use {{variable}} for dynamic content"
                      />
                    </div>

                    <div>
                                            <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          HTML Template
                        </label>
                        {htmlErrors.length > 0 && (
                          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            ⚠️ {htmlErrors.length} issue{htmlErrors.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      <div className="mt-1">
                        
                        <div className={`border rounded-md overflow-hidden ${
                          htmlErrors.length > 0 ? 'border-red-300' : 'border-gray-300'
                        }`}>
                          <textarea
                            value={formData.html_template}
                            onChange={(e) => setFormData({ ...formData, html_template: e.target.value })}
                            className="w-full p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            style={{
                              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                              backgroundColor: '#f8f9fa',
                              color: '#212529',
                              lineHeight: '1.5',
                              minHeight: '384px',
                              maxHeight: '500px'
                            }}
                            placeholder="HTML email template with {{variable}} placeholders"
                            spellCheck={false}
                          />
                        </div>
                      </div>
                      {htmlErrors.length > 0 && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">HTML Issues Detected</h3>
                              <div className="mt-2 text-sm text-red-700">
                                <ul className="list-disc list-inside space-y-1">
                                  {htmlErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Sample Data (JSON)
                      </label>
                      <textarea
                        value={formData.sample_data}
                        onChange={(e) => setFormData({ ...formData, sample_data: e.target.value })}
                        rows={6}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        placeholder='{"variable": "value"}'
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Variables (JSON Array)
                      </label>
                      <textarea
                        value={formData.variables}
                        onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        placeholder='["variable1", "variable2"]'
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={updateTemplateMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {updateTemplateMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        onClick={generatePreview}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Preview
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">

                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Description</h4>
                      <p className="mt-1 text-sm text-gray-900">{selectedTemplate.description}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Subject Template</h4>
                      <p className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                        {selectedTemplate.subject_template}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Available Variables</h4>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {selectedTemplate.variables.map((variable) => (
                          <span
                            key={variable}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {`{{${variable}}}`}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700">Sample Data</h4>
                      <pre className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(selectedTemplate.sample_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900">Select a Template</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Choose a template from the list to view and edit its content.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
             onClick={e => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg w-full sm:max-w-4xl max-h-[85vh] sm:max-h-[80vh] sm:mx-4 flex flex-col overflow-hidden">
            <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">Email Preview</h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto p-5 flex flex-col gap-4">
              <div className="border rounded-lg overflow-hidden">
                <iframe srcDoc={previewHtml} className="w-full h-96" title="Email Preview" />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => { const w = window.open(); if (w) { w.document.write(previewHtml); w.document.close(); } }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Open in New Tab
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Dialog */}
      {showTestEmailDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
             onClick={e => { if (e.target === e.currentTarget) { setShowTestEmailDialog(false); setTestEmailAddresses(''); } }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg w-full sm:max-w-sm sm:mx-4 flex flex-col">
            <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">Send Test Email</h3>
              <button onClick={() => { setShowTestEmailDialog(false); setTestEmailAddresses(''); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Send a test email using the current template and sample data to verify how it looks in an actual email client. You can send to multiple recipients by separating email addresses with commas.
              </p>
              {effectiveBrand !== DEFAULT_BRAND_KEY && (
                <p className="text-sm text-gray-600 mb-3">
                  Sent as {BRAND_PROFILES[effectiveBrand].shortName}: the sender name and the brand name, logo and
                  site in the email are {BRAND_PROFILES[effectiveBrand].shortName}&apos;s.
                </p>
              )}
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email Addresses (comma-separated)
              </label>
              <textarea
                value={testEmailAddresses}
                onChange={(e) => setTestEmailAddresses(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="recipient1@example.com, recipient2@example.com"
                rows={3}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowTestEmailDialog(false);
                  setTestEmailAddresses('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (testEmailAddresses && selectedTemplate) {
                    const recipients = testEmailAddresses.split(',').map(email => email.trim()).filter(email => email.length > 0);
                    if (recipients.length === 0) {
                      setToast({ message: 'Please enter at least one recipient email address.', type: 'error' });
                      return;
                    }
                    sendTestEmailMutation.mutate({
                      email: recipients.length === 1 ? recipients[0] : recipients,
                      templateKey: selectedTemplate.template_key,
                      brand: effectiveBrand,
                    });
                  }
                }}
                disabled={!testEmailAddresses || sendTestEmailMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendTestEmailMutation.isPending ? 'Sending...' : '📧 Send Test Email'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}


        {/* Toast Notifications */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
  );
}
